/**
 * Accessi e ruoli per sezione (NCI26-F3.0): i ruoli del portale (qa.admin, redflag.*, nc.*) raggruppati per sezione con
 * chi li ha, gli utenti (ruoli e stato), le richieste di accesso. Con l'SSO i ruoli li decide Entra. Nessuna mail automatica.
 */
import { Alert, App, Button, Card, Form, Input, Modal, Select, Table, Tag } from "antd";
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { RUOLI_PORTALE, sezioneDelRuolo, type AccessiDto, type RichiestaAccessoDto, type RuoloPortale, type UtenteDto } from "@qa/shared";
import { api } from "../../api/client";
import { fmtDataOra } from "../../lib/format";
import { useAggiorna } from "../../lib/query";
import { messaggioErrore } from "../../i18n/errori";
import { chiaveRuolo } from "../../i18n";
import { useAuth } from "../../auth/AuthContext";
import { Miss, PageHead, Stato, Vuoto } from "../../components/ui";

/** Opzioni dei ruoli raggruppate per sezione. */
function useOpzioniRuoli() {
  const { t } = useTranslation();
  const sezioni = [...new Set(RUOLI_PORTALE.map(sezioneDelRuolo))];
  return sezioni.map((s) => ({
    label: t(`dominio.sezioneRuoli.${s}`),
    options: RUOLI_PORTALE.filter((r) => sezioneDelRuolo(r) === s).map((r) => ({ value: r, label: `${t(`dominio.ruoloPortale.${chiaveRuolo(r)}`)} · ${r}` })),
  }));
}

function NuovoUtente({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const aggiorna = useAggiorna();
  const opzioni = useOpzioniRuoli();
  const [form] = Form.useForm<{ email: string; nome: string; ruoli: RuoloPortale[] }>();
  const m = useMutation({
    mutationFn: (v: { email: string; nome: string; ruoli: RuoloPortale[] }) => api.post<UtenteDto>("/admin/utenti", v),
    onSuccess: () => {
      message.success(t("admin.accessi.aggiunto"));
      void aggiorna();
      onClose();
    },
    onError: (e) => message.error(messaggioErrore(e)),
  });
  const obbl = [{ required: true, whitespace: true, message: t("redflag.nuova.obbligatorio") }];
  return (
    <Modal open onCancel={onClose} title={t("admin.accessi.invita")} okText={t("azioni.salva")} cancelText={t("azioni.annulla")} confirmLoading={m.isPending} onOk={() => form.submit()}>
      <p className="muted">{t("admin.accessi.invitaNota")}</p>
      <Form form={form} layout="vertical" initialValues={{ ruoli: ["redflag.direzione"] }} onFinish={(v) => m.mutate(v)}>
        <Form.Item name="email" label={t("admin.accessi.email")} rules={[...obbl, { type: "email", message: t("errore.datiNonValidi") }]}>
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item name="nome" label={t("admin.accessi.nome")} rules={obbl}>
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item name="ruoli" label={t("admin.accessi.ruoli")} rules={[{ required: true, type: "array", min: 1, message: t("admin.accessi.almenoUnRuolo") }]}>
          <Select mode="multiple" options={opzioni} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default function AccessiPage() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { me } = useAuth();
  const aggiorna = useAggiorna();
  const opzioni = useOpzioniRuoli();
  const [nuovo, setNuovo] = useState(false);
  const q = useQuery({ queryKey: ["accessi"], queryFn: () => api.get<AccessiDto>("/admin/accessi") });
  const fatto = { onSuccess: () => (message.success(t("admin.accessi.salvato")), void aggiorna(), void q.refetch()), onError: (e: unknown) => message.error(messaggioErrore(e)) };
  const utente = useMutation({ mutationFn: (a: { id: string; ruoli?: RuoloPortale[]; stato?: "attivo" | "disattivato" }) => api.patch<UtenteDto>(`/admin/utenti/${a.id}`, { ruoli: a.ruoli, stato: a.stato }), ...fatto });
  const richiesta = useMutation({ mutationFn: (a: { id: string; stato: "gestita" | "rifiutata" }) => api.patch(`/admin/richieste/${a.id}`, { stato: a.stato }), ...fatto });
  return (
    <>
      <PageHead
        crumbs={[{ label: t("portale.nome"), to: "/" }, { label: t("menu.gruppoAdmin") }, { label: t("menu.accessi") }]}
        title={t("admin.accessi.titolo")}
        sub={t("admin.accessi.sottotitolo")}
        extra={
          <Button type="primary" onClick={() => setNuovo(true)}>
            {t("admin.accessi.invita")}
          </Button>
        }
      />
      <Stato q={q}>
        {(a) => (
          <>
            {!a.ssoAttivo && <Alert type="warning" showIcon message={t("admin.accessi.ssoSpento")} style={{ marginBottom: 12 }} />}
            <Table
              size="middle"
              rowKey="ruolo"
              pagination={false}
              scroll={{ x: 640 }}
              dataSource={a.ruoli}
              columns={[
                { title: t("admin.accessi.sezione"), key: "s", width: 150, render: (_, r) => t(`dominio.sezioneRuoli.${r.sezione}`) },
                {
                  title: t("admin.accessi.ruolo"),
                  key: "r",
                  width: 240,
                  render: (_, r) => (
                    <>
                      {t(`dominio.ruoloPortale.${chiaveRuolo(r.ruolo)}`)}
                      <div className="mono muted small">
                        {r.appRole}
                        {r.alias.length > 0 && ` · ${t("admin.accessi.anche")} ${r.alias.join(", ")}`}
                      </div>
                    </>
                  ),
                },
                { title: t("admin.accessi.chi"), key: "c", render: (_, r) => (r.utenti.length ? r.utenti.map((u) => u.nome).join(", ") : <Miss>{t("admin.accessi.nessunoTest")}</Miss>) },
                { title: t("admin.accessi.cosaFa"), key: "d", render: (_, r) => t(`dominio.ruoloPortaleDescrizione.${chiaveRuolo(r.ruolo)}`) },
              ]}
            />
            <Alert type="info" showIcon message={t("admin.accessi.soloMario")} style={{ margin: "16px 0" }} />
            <Card size="small" title={t("admin.accessi.utenti")}>
              <Table<UtenteDto>
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={a.utenti}
                scroll={{ x: 700 }}
                columns={[
                  { title: t("admin.accessi.nome"), dataIndex: "nome" },
                  { title: t("admin.accessi.email"), dataIndex: "email" },
                  {
                    title: t("admin.accessi.ruoli"),
                    key: "r",
                    width: 320,
                    // con l'SSO i ruoli li decide Entra: qui solo in lettura
                    render: (_, u) =>
                      a.ssoAttivo || u.id === me?.id ? (
                        u.ruoli.map((r) => (
                          <Tag key={r} className="mono">
                            {r}
                          </Tag>
                        ))
                      ) : (
                        <Select<RuoloPortale[]>
                          size="small"
                          mode="multiple"
                          value={u.ruoli}
                          style={{ width: 300 }}
                          onChange={(ruoli) => ruoli.length && utente.mutate({ id: u.id, ruoli })}
                          options={opzioni}
                        />
                      ),
                  },
                  { title: t("admin.accessi.stato"), key: "s", width: 110, render: (_, u) => <Tag color={u.stato === "attivo" ? "success" : "default"}>{u.stato === "attivo" ? t("admin.accessi.attivo") : t("admin.accessi.disattivato")}</Tag> },
                  { title: t("admin.accessi.ultimoAccesso"), key: "u", width: 150, render: (_, u) => <span className="num">{fmtDataOra(u.ultimoAccesso)}</span> },
                  {
                    key: "a",
                    width: 110,
                    render: (_, u) =>
                      u.id !== me?.id && (
                        <Button size="small" onClick={() => utente.mutate({ id: u.id, stato: u.stato === "attivo" ? "disattivato" : "attivo" })}>
                          {u.stato === "attivo" ? t("admin.accessi.disattiva") : t("admin.accessi.riattiva")}
                        </Button>
                      ),
                  },
                ]}
              />
            </Card>
            <Card size="small" title={t("admin.accessi.richieste")} style={{ marginTop: 16 }}>
              {a.richieste.length ? (
                <Table<RichiestaAccessoDto>
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={a.richieste}
                  columns={[
                    { title: t("admin.accessi.nome"), key: "n", render: (_, r) => <>{r.nome}<div className="muted small">{r.email}</div></> },
                    { title: t("admin.accessi.motivo"), key: "m", render: (_, r) => r.motivo ?? "—" },
                    { title: t("admin.accessi.quando"), key: "q", width: 150, render: (_, r) => <span className="num">{fmtDataOra(r.at)}</span> },
                    { title: t("admin.accessi.stato"), key: "s", width: 100, render: (_, r) => t(`admin.accessi.statoRichiesta.${r.stato}`) },
                    {
                      key: "a",
                      width: 190,
                      render: (_, r) =>
                        r.stato === "aperta" && (
                          <>
                            <Button size="small" type="primary" onClick={() => richiesta.mutate({ id: r.id, stato: "gestita" })} style={{ marginRight: 4 }}>
                              {t("admin.accessi.gestita")}
                            </Button>
                            <Button size="small" danger onClick={() => richiesta.mutate({ id: r.id, stato: "rifiutata" })}>
                              {t("admin.accessi.rifiuta")}
                            </Button>
                          </>
                        ),
                    },
                  ]}
                />
              ) : (
                <Vuoto titolo={t("admin.accessi.nessunaRichiesta")} testo={t("admin.accessi.nessunaRichiestaNota")} />
              )}
            </Card>
          </>
        )}
      </Stato>
      {nuovo && <NuovoUtente onClose={() => setNuovo(false)} />}
    </>
  );
}
